/**
 * Aspect — Tape-measure tool
 * --------------------------------------------------------------
 * Click two points on structure (walls / floor / ceiling / furniture) to read
 * the straight-line distance. Works both pointer-locked (crosshair) and free
 * cursor. Markers + the connecting line are engine entities; the floating
 * readout label is rendered by the tour panel UI, which listens to
 * "measure:changed" events (DOM never touched from here).
 *
 * Toggle with the M key or the "ui:toggle-measure" event.
 */

import * as pc from "playcanvas";

const MARKER = 0.06;
const STRUCTURE_KINDS = new Set(["wall", "window", "floor", "ceiling"]);

function worldAABB(entity) {
  const aabb = new pc.BoundingBox();
  let ok = false;
  for (const render of entity.findComponents("render")) {
    for (const mi of render.meshInstances) {
      if (!ok) {
        aabb.copy(mi.aabb);
        ok = true;
      } else {
        aabb.add(mi.aabb);
      }
    }
  }
  return ok ? aabb : null;
}

function rayAABB(origin, dir, aabb) {
  const min = aabb.getMin();
  const max = aabb.getMax();
  let tmin = -Infinity;
  let tmax = Infinity;
  for (const axis of ["x", "y", "z"]) {
    const o = origin[axis];
    const d = dir[axis];
    if (Math.abs(d) < 1e-9) {
      if (o < min[axis] || o > max[axis]) return null;
    } else {
      let t1 = (min[axis] - o) / d;
      let t2 = (max[axis] - o) / d;
      if (t1 > t2) [t1, t2] = [t2, t1];
      tmin = Math.max(tmin, t1);
      tmax = Math.min(tmax, t2);
      if (tmin > tmax) return null;
    }
  }
  return tmax >= 0 ? (tmin >= 0 ? tmin : 0) : null;
}

export class MeasureTool {
  /**
   * @param {pc.AppBase} app
   * @param {pc.Entity} camera
   * @param {import('../scene_manager.js').SceneManager} scene
   */
  constructor(app, camera, scene) {
    this.app = app;
    this.camera = camera;
    this.scene = scene;
    this.active = false;
    this.a = null; // pc.Vec3
    this.b = null;
    this._root = null;
    this._mouse = { x: 0, y: 0 };

    this._mat = new pc.StandardMaterial();
    this._mat.diffuse = new pc.Color(1.0, 0.62, 0.1);
    this._mat.emissive = new pc.Color(1.0, 0.62, 0.1);
    this._mat.emissiveIntensity = 0.9;
    this._mat.useLighting = false;
    this._mat.update();

    app.mouse?.on(pc.EVENT_MOUSEMOVE, (e) => {
      this._mouse.x = e.x;
      this._mouse.y = e.y;
    });
    app.mouse?.on(pc.EVENT_MOUSEDOWN, (e) => {
      if (!this.active || e.button !== pc.MOUSEBUTTON_LEFT) return;
      this._click();
    });
    app.keyboard?.on(pc.EVENT_KEYDOWN, (e) => {
      if (e.key !== pc.KEY_M) return;
      const el = document.activeElement;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (!typing) this.toggle();
    });
    app.on("ui:toggle-measure", () => this.toggle());
    app.on("scene:room-loaded", () => this.clear());
    this._onUpdate = () => this._publish();
    app.on("update", this._onUpdate);
  }

  toggle() {
    this.setActive(!this.active);
  }

  setActive(on) {
    this.active = Boolean(on);
    if (!this.active) this.clear();
    this.app.fire("measure:state", { active: this.active });
  }

  clear() {
    this.a = null;
    this.b = null;
    this._root?.destroy();
    this._root = null;
  }

  _pickCoords() {
    const canvas = this.app.graphicsDevice?.canvas;
    if (canvas && document.pointerLockElement === canvas) {
      return { x: canvas.clientWidth / 2, y: canvas.clientHeight / 2 };
    }
    return { x: this._mouse.x, y: this._mouse.y };
  }

  /** Nearest hit point among structure, decor-free furniture, and the floor. */
  _raycast() {
    const cam = this.camera.camera;
    const { x, y } = this._pickCoords();
    const near = cam.screenToWorld(x, y, cam.nearClip);
    const far = cam.screenToWorld(x, y, cam.farClip);
    const dir = far.clone().sub(near).normalize();

    let bestT = Infinity;
    const consider = (entity) => {
      const aabb = worldAABB(entity);
      if (!aabb) return;
      const t = rayAABB(near, dir, aabb);
      if (t !== null && t > 0.05 && t < bestT) bestT = t;
    };

    const visit = (entity) => {
      for (const child of entity.children) {
        if (STRUCTURE_KINDS.has(child._aspectKind)) consider(child);
        visit(child);
      }
    };
    if (this.scene.roomRoot) visit(this.scene.roomRoot);
    for (const entity of this.scene.entities?.values() ?? []) {
      if (entity.enabled) consider(entity);
    }

    if (!Number.isFinite(bestT)) return null;
    return new pc.Vec3(
      near.x + dir.x * bestT,
      near.y + dir.y * bestT,
      near.z + dir.z * bestT,
    );
  }

  _click() {
    const hit = this._raycast();
    if (!hit) return;
    if (!this.a || (this.a && this.b)) {
      // Start a fresh measurement.
      this.clear();
      this.a = hit;
    } else {
      this.b = hit;
    }
    this._rebuildMarkers();
  }

  _rebuildMarkers() {
    this._root?.destroy();
    this._root = null;
    if (!this.a) return;
    const root = new pc.Entity("measure-root");
    this._root = root;
    this.app.root.addChild(root);

    const marker = (name, p) => {
      const m = new pc.Entity(name);
      m.addComponent("render", { type: "sphere", material: this._mat, castShadows: false });
      m.setLocalScale(MARKER, MARKER, MARKER);
      m.setLocalPosition(p.x, p.y, p.z);
      root.addChild(m);
    };
    marker("measure-a", this.a);
    if (this.b) {
      marker("measure-b", this.b);
      const mid = new pc.Vec3().add2(this.a, this.b).mulScalar(0.5);
      const d = new pc.Vec3().sub2(this.b, this.a);
      const len = d.length();
      if (len > 1e-4) {
        const line = new pc.Entity("measure-line");
        line.addComponent("render", { type: "box", material: this._mat, castShadows: false });
        line.setLocalScale(0.014, 0.014, len);
        line.setLocalPosition(mid.x, mid.y, mid.z);
        line.lookAt(this.b.x, this.b.y, this.b.z);
        root.addChild(line);
      }
    }
    this.app.renderNextFrame = true;
  }

  /** Stream the readout (distance + screen anchor) to the UI each frame. */
  _publish() {
    if (!this.active || !this.a) return;
    const b = this.b ?? this._raycast();
    if (!b) {
      this.app.fire("measure:changed", { meters: null });
      return;
    }
    const meters = this.a.distance(b);
    const mid = new pc.Vec3().add2(this.a, b).mulScalar(0.5);
    const sv = this.camera.camera.worldToScreen(mid);
    this.app.fire("measure:changed", {
      meters,
      fixed: Boolean(this.b),
      screen: sv.z > 0 ? { x: sv.x, y: sv.y } : null,
    });
  }

  destroy() {
    this.app.off("update", this._onUpdate);
    this.clear();
    this._mat = null;
  }
}

export default MeasureTool;
